<template>
  <payment-link-layout
    v-if="paymentLink && checkout"
    ref="payment-link-layout"
    :isLoading="loading"
  >
    <template v-slot:content>
      <step-progress-bar
        class="mt-24"
        stepTitle="Endereço de Cobrança"
        :stepNumber="1"
        :stepSize="4"
      />

      <form class="mt-24 pt-24" name="verifyCreditComplementForm">
        <p class="small-regular gray-500">CEP</p>
        <p class="small-regular gray-900 mt-4 mb-16">{{ checkout.zipCode | zipCodeFilter }}</p>

        <template v-if="checkout.addressStreet">
          <p class="small-regular gray-500">Endereço</p>
          <p class="small-regular gray-900 mt-4 mb-20">{{ checkout.addressStreet }}</p>
        </template>

        <app-input
          v-if="!checkout.addressStreet"
          label="Endereço"
          class="app-input"
          v-model="user.addressStreet"
          @input="handleFormInput('addressStreet', ...arguments)"
          :errorMessage="getMessage('addressStreet')"
        />

        <div class="complement-info">
          <app-input
            label="Número"
            class="app-input address-number"
            v-model="user.addressNumber"
            @input="handleFormInput('addressNumber', ...arguments)"
            :errorMessage="getMessage('addressNumber')"
          />

          <app-input
            label="Complemento"
            class="app-input address-complement"
            optional="(opcional)"
            v-model="user.addressComplement"
            placeholder="Apto, casa, sala, bloco..."
            @input="handleFormInput('addressComplement', ...arguments)"
            :errorMessage="getMessage('addressComplement')"
          />
        </div>

      </form>
    </template>

    <template v-slot:footer>
      <round-button
        color="primary mt-12"
        @click="handleVerifyCreditComplement"
        content="Continuar"
      />
    </template>
  </payment-link-layout>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { mask } from 'vue-the-mask';
import UtilMixin from '@/mixins/util';
import StringMixin from '@/mixins/string';
import PaymentLinkLayout from '../layout/PaymentLinkLayout.vue';
import AppInput from '../../../components/ui/AppInput.vue';
import RoundButton from '../components/RoundButton.vue';
import StepProgressBar from '../components/StepProgressBar.vue';

export default {
  name: 'PaymentLinkVerifyCreditComplement',
  components: {
    PaymentLinkLayout,
    AppInput,
    RoundButton,
    StepProgressBar,
  },
  directives: {
    mask,
  },
  mixins: [UtilMixin, StringMixin],
  data: () => ({
    formInputs: {
      addressStreet: {
        isValid: false,
        validateFunction: 'isFieldFilled',
        errorMessage: 'Endereço inválido',
        currentMessage: '',
      },
      addressNumber: {
        isValid: false,
        validateFunction: 'isFieldFilled',
        errorMessage: 'Número inválido',
        currentMessage: '',
      },
      addressComplement: {
        isValid: true,
        errorMessage: '',
        currentMessage: '',
      },
    },
  }),
  computed: {
    ...mapState('paymentLink', [
      'user',
      'nextRoute',
      'checkout',
      'paymentLink',
      'loading',
    ]),
    isValidForm() {
      let isValid = true;
      const keys = Object.keys(this.formInputs);
      for (let i = 0; i < keys.length; i += 1) {
        const key = keys[i];
        if (!this.formInputs[key].isValid) {
          isValid = false;
          break;
        }
      }
      return isValid;
    },
  },
  methods: {
    ...mapActions('paymentLink', [
      'setUserData',
      'verifyCredit',
      'applyComplementForPaymentLink',
      'getPaymentLinkCheckout',
      'setLoading',
    ]),
    async handleVerifyCreditComplement() {
      if (!this.isValidForm) {
        this.handleErrorMessages();
        return;
      }
      try {
        await this.applyComplementForPaymentLink();

        await this.$router.replace({
          name: this.nextRoute,
          query: this.$route.query,
        });
      } catch (error) {
        console.log(error);
      }
    },
    handleFormInput(field, value) {
      if (!value) return;
      this.setUserData({ field, value });
      const { validateFunction } = this.formInputs[field];
      this.formInputs[field].isValid = (this.validateFunction && this[validateFunction](value))
        || true;
      if (this.formInputs[field].currentMessage !== '') {
        this.formInputs[field].currentMessage = '';
      }
    },
    handleErrorMessages() {
      Object.keys(this.formInputs).forEach((key) => {
        if (!this.formInputs[key].isValid) {
          this.formInputs[key].currentMessage = this.formInputs[key].errorMessage;
        }
      });
    },
    getMessage(field) {
      return this.formInputs[field].currentMessage;
    },
    isFieldFilled(value) {
      return !!value;
    },
  },
  async mounted() {
    this.setLoading({ loading: true });
    await this.getPaymentLinkCheckout();
    if (this.checkout.addressStreet) {
      this.setUserData({ field: 'addressStreet', value: this.checkout.addressStreet });
      this.formInputs.addressStreet.isValid = true;
    }
    this.setLoading({ loading: false });
  },
};
</script>

<style lang="scss" scoped>
  @import '../../../scss/__new-styles.scss';

  ::v-deep .app-input input {
    display: block;
    width: 100%;
    border-radius: 0.375rem;
    padding: 8px 12px;
    background-color: $gray-50;
    height: 52px;

    @media (min-width: $md) {
      height: 48px;
    }
  }

  .checkbox-error-message {
    font-family: Manrope;
    color: var(--danger-dark);
    font-size: var(--font-size-sm);
    line-height: var(--line-height-sm);
  }

  .complement-info {
    display: flex;
    gap: 20px;
    width: 100%;

    ::v-deep .app-input {
      &.address-number {
        flex: 1 1 0px;
      }

      &.address-complement {
        flex: 2 2 0px;
      }
    }

    @media (max-width: $breakpoint-large) {
      flex-direction: column;
      gap: 0px;

      ::v-deep .app-input {
        &.address-number {
          width: 100%;
        }

        &.address-complement {
          width: 100%;
        }
      }
    }
  }

  .pt-24 {
    padding-top: 24px;
  }

</style>
